<template>
  <div>
    <hs-code-list-add-new
      :is-add-new-h-s-code-sidebar-active.sync="isAddNewHSCodeSidebarActive"
      @refetch-data="refetchData"
    />
    <hs-code-list-edit
      :selected-h-s-code-id="selectedHSCodeIdForEdit"
      @refetch-data="refetchData"
      @updated="selectedHSCodeIdForEdit = null"
    />
    <hs-code-list-view
      :selected-h-s-code-id="selectedHSCodeIdForView"
      @close="selectedHSCodeIdForView = null"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="4">
            <div class="d-flex align-items-center justify-content-center">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="Search..."
              />
            </div>
          </b-col>

          <!-- Actions -->
          <b-col cols="12" md="4">
            <div class="d-flex align-items-center justify-content-end">
              <!-- Watch Now Button -->
              <b-button
                variant="outline-success"
                size="sm"
                class="mr-2"
                @click="openWatchVideo"
              >
                <feather-icon icon="PlayIcon" size="16" class="mr-1" />
                Watch Now
              </b-button>

              <b-button variant="primary" @click="isAddNewHSCodeSidebarActive = true">
                <span class="text-nowrap">Add HS Code</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Filters -->
        <!-- <hs-code-list-filters :is-active-filter.sync="isActiveFilter" /> -->
      </div>

      <b-table
        ref="refHSCodeListTable"
        class="position-relative"
        :items="fetchHSCodes"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No matching records found"
        :busy="isTableLoading"
        sticky-header="true"
      >
        <!-- Loading Skeleton -->
        <template #table-busy>
          <table-skeleton :columns="tableColumns" :rows="5" />
        </template>
        <!-- Column: ID -->
        <template #cell(id)="data">
          {{ data.item.id }}
        </template>

        <!-- Column: Code -->
        <template #cell(code)="data">
          <span
            class="font-weight-bold text-primary cursor-pointer hover-effect"
            @click="viewHSCode(data.item.id)"
            title="Click to view HS Code details"
          >
            {{ data.value }}
          </span>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <div class="d-flex">
            <h6 class="mb-0">{{ data.value }}</h6>
          </div>
        </template>

        <!-- Column: Description -->
        <template #cell(description)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Is Active -->
        <template #cell(isActive)="data">
          <b-badge :variant="getStatusVariant(data.value)" pill class="status-badge">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <!-- Column: Created By -->
        <template #cell(createdBy)="data">
          <timestamp-cell :timestamp="data.item.addedOn" :byUser="data.item.metaAddedBy" />
        </template>

        <!-- Column: Updated By -->
        <template #cell(updatedBy)="data">
          <timestamp-cell :timestamp="data.item.updatedOn" :byUser="data.item.metaUpdatedBy" />
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <!-- View Button -->
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              title="View HS Code"
              @click="viewHSCode(data.item.id)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>

            <!-- Edit Button -->
            <b-button
              variant="flat-info"
              class="btn-icon rounded-circle"
              title="Edit HS Code"
              @click="selectedHSCodeIdForEdit = data.item.id"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>

            <!-- Delete Button -->
            <b-button
              variant="flat-danger"
              class="btn-icon rounded-circle"
              title="Delete HS Code"
              @click="onDeleteHSCode(data.item.id)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalHSCodes"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import store from '@/store';
import { onUnmounted, ref, watch } from '@vue/composition-api';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import hsCodeStoreModule from './HSCodeStoreModule';
import HSCodeListAddNew from './HSCodeListAddNew.vue';
import HSCodeListEdit from './HSCodeListEdit.vue';
import HSCodeListView from './HSCodeListView.vue';
import HSCodeListFilters from './HSCodeListFilters.vue';
import useHSCodeList from './useHSCodeList';
import TimestampCell from '@/components/common/list/TimestampCell.vue';
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';

export default {
  components: {
    'hs-code-list-add-new': HSCodeListAddNew,
    'hs-code-list-edit': HSCodeListEdit,
    'hs-code-list-view': HSCodeListView,
    'hs-code-list-filters': HSCodeListFilters,
    TimestampCell,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
    TableSkeleton,
  },
  setup(props, { root }) {
    const HS_CODE_APP_STORE_MODULE_NAME = 'fbr-invoice-hs-code';

    // Register module
    if (!store.hasModule(HS_CODE_APP_STORE_MODULE_NAME))
      store.registerModule(HS_CODE_APP_STORE_MODULE_NAME, hsCodeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(HS_CODE_APP_STORE_MODULE_NAME))
        store.unregisterModule(HS_CODE_APP_STORE_MODULE_NAME);
    });

    const isAddNewHSCodeSidebarActive = ref(false);
    const selectedHSCodeIdForEdit = ref(null);
    const selectedHSCodeIdForView = ref(null);

    const resetSelectedHSCodeId = () => {
      selectedHSCodeIdForEdit.value = null;
    };

    const onDeleteHSCode = async (id) => {
      try {
        // Show confirmation dialog using root.$bvModal
        const confirmed = await new Promise((resolve) => {
          root.$bvModal
            .msgBoxConfirm('Are you sure you want to delete this HS Code?', {
              title: 'Please Confirm',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'YES',
              cancelTitle: 'NO',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true,
            })
            .then((value) => resolve(value));
        });

        if (!confirmed) return;

        // Call delete action
        await store.dispatch('fbr-invoice-hs-code/deleteHSCode', { id });

        // Show success toast
        root.$bvToast.toast('HS Code deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });

        // Refetch data to update the table
        refetchData();
      } catch (error) {
        console.error('Error deleting HS Code:', error);

        // Show user-friendly error message
        let errorMessage = 'Error deleting HS Code. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    };

    const viewHSCode = (id) => {
      selectedHSCodeIdForView.value = id;
      // The view sidebar will open automatically
    };

    const {
      fetchHSCodes,
      tableColumns,
      perPage,
      currentPage,
      totalHSCodes,
      dataMeta,
      perPageOptions,
      searchQuery: composableSearchQuery,
      refHSCodeListTable,
      refetchData,
      isTableLoading,
      isActiveFilter,
    } = useHSCodeList();

    // Video link for Watch Now button
    const hsCodeVideoLink = 'https://cdn.cloudious.net/file-1756984677138-673188013.mp4';

    // Watch video function
    const openWatchVideo = () => {
      window.open(hsCodeVideoLink, '_blank');
    };

    watch(currentPage, (newVal) => {
      console.log('=== currentPage changed:', newVal);
    });

    // Local search query for debouncing
    const searchQuery = ref('');

    // Watch local search query and sync with composable
    watch(searchQuery, (newValue) => {
      composableSearchQuery.value = newValue;
    });

    // Helper function for status variant
    const getStatusVariant = (isActive) => {
      return isActive ? 'light-success' : 'light-danger';
    };

    return {
      // Sidebar
      isAddNewHSCodeSidebarActive,
      selectedHSCodeIdForEdit,
      selectedHSCodeIdForView,

      tableColumns,
      perPage,
      currentPage,
      totalHSCodes,
      dataMeta,
      perPageOptions,
      searchQuery,
      refHSCodeListTable,

      fetchHSCodes,
      refetchData,
      resetSelectedHSCodeId,
      onDeleteHSCode,
      viewHSCode,
      isTableLoading,

      // Filter states
      isActiveFilter,

      // Helper functions
      getStatusVariant,
      openWatchVideo,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.b-table-sticky-header {
  max-height: 80vh !important;
}

// Clickable code styling
.cursor-pointer {
  cursor: pointer;
}

.hover-effect {
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0056b3 !important;
    text-decoration: underline;
    transform: translateY(-1px);
  }
}

// Skeleton loader styles
.skeleton-row {
  .skeleton-cell {
    padding: 0.75rem;

    .skeleton-item {
      height: 20px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Enhanced badge styling for status
.status-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Light variant badge styles
.badge.badge-pill {
  &.light-success {
    background-color: #d1f2eb;
    color: #0f5132;
    border: 1px solid #badbcc;
  }

  &.light-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
